import { Box, Typography, Button, Paper, Card, CardContent, Stack } from '@mui/material';
import { useTheme, TypographyVariant } from '@mui/material/styles';
import { PaletteColor } from '@mui/material';
import luisa from '@/theme/luisa';

type ColorPaletteProps = {
    title: string;
    colorPalette: Record<string, unknown> | PaletteColor;
    paletteName: string;
};

const ColorPaletteDisplay = ({ title, colorPalette, paletteName }: ColorPaletteProps) => {
    const theme = useTheme();

    return (
        <Box>
            <Typography variant="h6" mb={0.5}>
                {title} (theme.palette.{paletteName})
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {Object.entries(colorPalette).map(([key, value]) => {
                    // Skip contrastText
                    if (key === 'contrastText') return null;

                    if (typeof value === 'string') {
                        const borderColor = paletteName === 'white' ? theme.palette.gray['30'] : theme.palette.white['100'];

                        return (
                            <Box key={key} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                <Box
                                    key={key}
                                    sx={{
                                        width: '150px',
                                        height: '50px',
                                        backgroundColor: value,
                                        border: `1px solid ${borderColor}`,
                                    }}
                                />
                                <Typography variant="caption">
                                    {paletteName}.{key} ({value})
                                </Typography>
                            </Box>
                        );
                    }
                    return null;
                })}
            </Box>
        </Box>
    );
};

const ThemePreview = () => {
    const theme = useTheme();
    const spacingFactors = Object.values(luisa.spacings);
    const spacingIndices = Array.from({ length: spacingFactors.length }, (_, i) => i);

    return (
        <Stack spacing={3} m={2}>
            {/* Colors */}
            <Card>
                <CardContent>
                    <Typography variant="h3" mb={2}>
                        MUI theme colors
                    </Typography>
                    <Stack spacing={1.5}>
                        <ColorPaletteDisplay title="Primary" colorPalette={theme.palette.primary} paletteName="primary" />
                        <ColorPaletteDisplay title="Secondary" colorPalette={theme.palette.secondary} paletteName="secondary" />
                        <ColorPaletteDisplay title="Error" colorPalette={theme.palette.error} paletteName="error" />
                        <ColorPaletteDisplay title="Success" colorPalette={theme.palette.success} paletteName="success" />
                        <ColorPaletteDisplay title="Info" colorPalette={theme.palette.info} paletteName="info" />
                    </Stack>
                </CardContent>
            </Card>
            <Card>
                <CardContent>
                    <Typography variant="h3" mb={2}>
                        Luisa color swatches mapped to MUI theme palette
                    </Typography>
                    <Stack spacing={1.5}>
                        <ColorPaletteDisplay title="Leadblue" colorPalette={theme.palette.leadblue} paletteName="leadblue" />
                        <ColorPaletteDisplay title="Devblue" colorPalette={theme.palette.devblue} paletteName="devblue" />
                        <ColorPaletteDisplay title="Headset" colorPalette={theme.palette.headset} paletteName="headset" />
                        <ColorPaletteDisplay title="Gray" colorPalette={theme.palette.gray} paletteName="gray" />
                        <ColorPaletteDisplay title="Green" colorPalette={theme.palette.green} paletteName="green" />
                        <ColorPaletteDisplay title="Yellow" colorPalette={theme.palette.yellow} paletteName="yellow" />
                        <ColorPaletteDisplay title="Red" colorPalette={theme.palette.red} paletteName="red" />
                        <ColorPaletteDisplay title="Pink" colorPalette={theme.palette.pink} paletteName="pink" />
                        <ColorPaletteDisplay title="White" colorPalette={theme.palette.white} paletteName="white" />
                    </Stack>
                </CardContent>
            </Card>

            {/* Typography variants */}
            <Card>
                <CardContent>
                    <Typography variant="h3" mb={2}>
                        Typography variants
                    </Typography>
                    <Stack spacing={1.5}>
                        {['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'body1', 'body2', 'subtitle1'].map((variant) => (
                            <Typography key={variant} variant={variant as TypographyVariant}>
                                Variant: {variant}
                            </Typography>
                        ))}
                    </Stack>
                </CardContent>
            </Card>

            {/* Spacings */}
            <Card>
                <CardContent>
                    <Typography variant="h3" mb={2}>
                        Spacings
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
                        {spacingIndices.map((index) => {
                            const spacingFactor = spacingFactors[index];
                            const spacingValue = theme.spacing(spacingFactor);

                            return (
                                <Box key={spacingValue} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            width: theme.spacing(spacingValue).replace('rem', ''),
                                            height: '24px',
                                            backgroundColor: theme.palette.primary.main,
                                        }}
                                    />
                                    <Typography variant="caption">{spacingFactor}</Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </CardContent>
            </Card>

            {/* Shadows */}
            <Card>
                <CardContent>
                    <Typography variant="h3" mb={2}>
                        Shadows
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4, flexWrap: 'wrap' }}>
                        {theme.shadows.slice(0, 5).map((shadow, index) => (
                            <Box
                                key={index}
                                boxShadow={index}
                                sx={{
                                    display: 'flex',
                                    width: '100px',
                                    height: '100px',
                                    backgroundColor: 'white',
                                    boxShadow: shadow,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Typography variant="caption">shadow: {index}</Typography>
                            </Box>
                        ))}
                    </Box>
                </CardContent>
            </Card>

            {/* Component examples */}
            <Card>
                <CardContent>
                    <Typography variant="h3" mb={2}>
                        Component examples
                    </Typography>
                    <Stack spacing={3}>
                        <Box>
                            <Typography variant="h5" mb={1}>
                                Buttons
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                {[
                                    { variant: 'contained' as const, color: 'primary' as const, label: 'Primary Button' },
                                    { variant: 'contained' as const, color: 'secondary' as const, label: 'Secondary Button' },
                                    { variant: 'outlined' as const, color: 'primary' as const, label: 'Outlined Button' },
                                    { variant: 'text' as const, color: 'primary' as const, label: 'Text Button' },
                                    { variant: 'contained' as const, color: 'primary' as const, label: 'Disabled Button', disabled: true },
                                ].map((button, index) => (
                                    <Button key={index} variant={button.variant} color={button.color} disabled={button.disabled}>
                                        {button.label}
                                    </Button>
                                ))}
                            </Box>
                        </Box>
                        <Box>
                            <Typography variant="h5" mb={1}>
                                Paper
                            </Typography>
                            <Paper sx={{ p: 2 }}>
                                <Typography>Paper example</Typography>
                            </Paper>
                        </Box>
                        <Box>
                            <Typography variant="h5" mb={1}>
                                Card
                            </Typography>
                            <Card sx={{ maxWidth: 345 }}>
                                <CardContent>
                                    <Typography variant="h5">Card example</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Cards contain content and actions about a single subject.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>
        </Stack>
    );
};

export default ThemePreview;
